import Phaser from 'phaser';
import { StateMachine, gridMove, Direction, clockwise, counterClockwise, oppositeDirection } from 'tower/util';
import BattleScene from 'tower/scenes/battle';
import Enemy, { EnemyOptions, EnemyState } from 'tower/scenes/battle/enemy';
import gameState from 'tower/state/game-state';

const MOVE_SPEED = 60;

interface WispOptions extends EnemyOptions {
  pattern?: string;
  rotation?: 'clockwise' | 'counterClockwise';
  still?: boolean;
}

export default class Wisp extends Enemy {
  pattern: Direction[] | null = null;
  patternIndex = 0;

  constructor(
    scene: BattleScene,
    x: number,
    y: number,
    public wispOptions: WispOptions = {},
  ) {
    super(scene, x, y, 'enemies', 70, 'wisp', {
      hp: 200,
      touchDamage: gameState.hasItem('wispRing') ? undefined : 1,
      knockback: false,
      passthroughWalls: true,
      damageFlash: false,
    });
    this.body.setSize(12, 12);

    if (wispOptions.pattern) {
      this.pattern = wispOptions.pattern.split('').map((s) => {
        switch (s) {
          case 'u':
            return Direction.Up;
          case 'd':
            return Direction.Down;
          case 'l':
            return Direction.Left;
          case 'r':
          default:
            return Direction.Right;
        }
      });
    }

    if (!gameState.hasItem('wispRing')) {
      const handleItemChange = () => {
        if (gameState.hasItem('wispRing')) {
          this.touchDamage = null;
          gameState.off('itemchange', handleItemChange);
        }
      };
      gameState.on('itemchange', handleItemChange);
      this.on('destroy', () => gameState.off('itemchange', handleItemChange));
    }
  }

  makeStateMachine(scene: BattleScene): StateMachine<EnemyState> {
    return new StateMachine('moving', {
      moving: new WispMovingState(scene, this),
    });
  }

  static create(scene: BattleScene) {
    scene.anims.create({
      key: `wisp`,
      frameRate: 8,
      frames: scene.anims.generateFrameNumbers('enemies', {
        frames: [70, 78, 86, 94],
      }),
      repeat: -1,
    });
  }
}

class WispState extends EnemyState {
  declare enemy: Wisp;
}

class WispMovingState extends WispState {
  targetX!: number;
  targetY!: number;
  reached = true;

  async handleEntered() {
    const { enemy, scene } = this;
    if (gameState.getFlag('disableFlashing')) {
      enemy.stop();
      enemy.setFrame(70);
    } else {
      enemy.play('wisp', true);
    }

    // If we did not reach our previous target, we got interrupted (e.g. hit) and should keep going.
    if (!this.reached) {
      return;
    }
    this.reached = false;

    const maze = scene.currentRoom.maze;

    // If a pattern is defined, follow it. Otherwise, turn if possible.
    let nextDirection = enemy.direction;
    if (enemy.pattern !== null) {
      nextDirection = enemy.pattern[enemy.patternIndex];
      enemy.patternIndex = (enemy.patternIndex + 1) % enemy.pattern.length;
    } else {
      const openDirections = maze.getOpenDirectionsAtWorldXY(enemy.x, enemy.y);

      const turn = enemy.wispOptions.rotation === 'counterClockwise' ? counterClockwise : clockwise;
      const oppositeTurn = enemy.wispOptions.rotation === 'counterClockwise' ? clockwise : counterClockwise;
      const checkDirections = [turn(enemy.direction)];
      for (let k = 0; k < 3; k++) {
        checkDirections.push(oppositeTurn(checkDirections.at(-1)!));
      }

      for (const checkDirection of checkDirections) {
        // Do not check backwards unless there is only one open direction and we must reverse
        if (checkDirection === oppositeDirection(enemy.direction) && openDirections.length > 1) {
          continue;
        }

        if (openDirections.includes(checkDirection)) {
          nextDirection = checkDirection;
          break;
        }
      }
    }

    const [spaceX, spaceY] = maze.worldXYToSpaceXY(enemy.x, enemy.y);
    const [newSpaceX, newSpaceY] = gridMove(spaceX, spaceY, nextDirection, 1);
    const [newWorldX, newWorldY] = maze.spaceXYToWorldXY(newSpaceX, newSpaceY);

    this.targetX = newWorldX;
    this.targetY = newWorldY;

    enemy.direction = nextDirection;
  }

  update() {
    const { enemy, scene } = this;

    // Do nothing if we're not meant to move.
    if (enemy.wispOptions.still) {
      return;
    }

    scene.physics.moveTo(enemy, this.targetX, this.targetY, MOVE_SPEED);
    if (Phaser.Math.Distance.Between(enemy.x, enemy.y, this.targetX, this.targetY) <= 1) {
      enemy.body.reset(this.targetX, this.targetY);
      this.reached = true;
      return this.transition('moving');
    }
  }
}
